import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown, Plus, Minus } from 'lucide-react';

interface NumberInputProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    className?: string;
    placeholder?: string;
}

export const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    min = 0,
    max,
    step = 1,
    className = '',
    placeholder
}) => {
    // Local state for free typing before commit
    const [localValue, setLocalValue] = useState(value.toString());

    useEffect(() => {
        setLocalValue(value.toString());
    }, [value]);

    const handleBlur = () => {
        let numericVal = parseInt(localValue || '0', 10);
        if (isNaN(numericVal)) numericVal = min;

        // Clamp
        if (min !== undefined && numericVal < min) numericVal = min;
        if (max !== undefined && numericVal > max) numericVal = max;

        // Update parent
        onChange(numericVal);
        setLocalValue(numericVal.toString());
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Allow empty string for backspacing everything
        if (e.target.value === '') {
            setLocalValue('');
            return;
        }

        // Only allow numeric input
        if (/^\d*$/.test(e.target.value)) {
            setLocalValue(e.target.value);
        }
    };

    const increment = () => {
        const current = parseInt(localValue || '0', 10);
        let next = current + step;
        if (max !== undefined && next > max) next = max;
        onChange(next);
    };

    const decrement = () => {
        const current = parseInt(localValue || '0', 10);
        let next = current - step;
        if (min !== undefined && next < min) next = min;
        onChange(next);
    };

    return (
        <div className={`number-input-container ${className}`}>
            <button
                type="button"
                className="number-input-btn decrease"
                onClick={decrement}
                disabled={min !== undefined && value <= min}
                tabIndex={-1}
            >
                <Minus size={14} />
            </button>
            <input
                type="text"
                className="number-input-field"
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
            />
            <button
                type="button"
                className="number-input-btn increase"
                onClick={increment}
                disabled={max !== undefined && value >= max}
                tabIndex={-1}
            >
                <Plus size={14} />
            </button>
        </div>
    );
};
